import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingDown, Upload, Users, Sparkles, Monitor, AlertTriangle, Loader2, MessageSquare } from "lucide-react";

const pct = (n) => `${(n ?? 0).toFixed ? n.toFixed(1) : n}%`;

export default function ActivationFunnel() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/events/summary?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null)),
      api.get(`/admin/activation-survey/summary?days=${days}`).then((r) => setSurvey(r.data)).catch(() => setSurvey(null)),
    ]).finally(() => setLoading(false));
  }, [days]);

  const funnel = data?.funnel || [];
  const first = funnel[0]?.users || 0;
  // biggest step-to-step drop
  let worstIdx = -1, worstDrop = 0;
  for (let i = 1; i < funnel.length; i++) {
    const drop = (funnel[i - 1].users || 0) - (funnel[i].users || 0);
    if (drop > worstDrop) { worstDrop = drop; worstIdx = i; }
  }
  const uh = data?.upload_health || {};
  const sm = data?.sample || {};
  const devices = data?.devices || {};
  const choices = data?.welcome_choices || {};

  return (
    <div data-testid="activation-funnel-page" className="max-w-5xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader title="Activation Funnel" subtitle="Where trial users drop off between signup and their first Excel upload." />
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-36 rounded-sm" data-testid="funnel-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground p-10"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : !data ? (
        <div className="p-10 text-muted-foreground" data-testid="funnel-empty">No analytics available yet.</div>
      ) : (
        <div className="space-y-6">
          {/* Funnel */}
          <div className="border border-border bg-card p-6 rounded-sm" data-testid="funnel-steps">
            <div className="flex items-center gap-2 mb-4"><Users className="h-4 w-4 text-[#0B3D91]" /><h3 className="font-heading font-bold">Signup → Upload funnel</h3></div>
            {first === 0 && <p className="text-sm text-muted-foreground mb-3">No signups recorded in this window yet — events will populate as trial users flow through.</p>}
            <div className="space-y-2.5">
              {funnel.map((s, i) => {
                const w = first ? Math.max(2, Math.round(100 * s.users / first)) : 0;
                const isDrop = i === worstIdx;
                return (
                  <div key={s.name} data-testid={`funnel-step-${s.name}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-foreground">{i + 1}. {s.label} {isDrop && <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-bold text-red-600"><TrendingDown className="h-3 w-3" />biggest drop</span>}</span>
                      <span className="text-muted-foreground"><b className="text-foreground font-mono">{s.users}</b> · {pct(s.pct_of_first)} of signups · {pct(s.pct_of_prev)} of prev</span>
                    </div>
                    <div className="h-3 w-full bg-secondary rounded-sm overflow-hidden">
                      <div className={`h-full rounded-sm ${isDrop ? "bg-red-500" : "bg-[#0B3D91]"}`} style={{ width: `${w}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Upload health */}
            <div className="border border-border bg-card p-6 rounded-sm" data-testid="funnel-upload-health">
              <div className="flex items-center gap-2 mb-3"><Upload className="h-4 w-4 text-[#16A34A]" /><h3 className="font-heading font-bold">Upload health — broken vs abandoned</h3></div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <Stat label="Started" value={uh.started_users ?? 0} />
                <Stat label="Completed" value={uh.completed_users ?? 0} green />
                <Stat label="Completion" value={pct(uh.completion_rate)} />
              </div>
              <div className="mt-4">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Failures: {uh.failed_events ?? 0}</div>
                {Object.keys(uh.failures_by_stage || {}).length > 0 ? (
                  <ul className="mt-1.5 text-sm text-muted-foreground space-y-1">
                    {Object.entries(uh.failures_by_stage).map(([st, n]) => <li key={st} className="flex justify-between"><span>stage: {st}</span><b className="font-mono text-foreground">{n}</b></li>)}
                  </ul>
                ) : <p className="text-sm text-muted-foreground mt-1">No upload failures recorded — drop-off is abandonment, not bugs.</p>}
              </div>
            </div>

            {/* Sample correlation */}
            <div className="border border-border bg-card p-6 rounded-sm" data-testid="funnel-sample">
              <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-[#0B3D91]" /><h3 className="font-heading font-bold">Sample portfolio → upload</h3></div>
              <p className="text-xs text-muted-foreground mb-3">Do users who explore the sample upload their own data more, or less?</p>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="border border-border rounded-sm p-3">
                  <div className="text-2xl font-extrabold font-mono text-[#0B3D91]">{pct(sm.explored_upload_rate)}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Explorers who uploaded<br />({sm.explored_uploaded ?? 0}/{sm.explored_users ?? 0})</div>
                </div>
                <div className="border border-border rounded-sm p-3">
                  <div className="text-2xl font-extrabold font-mono text-slate-700">{pct(sm.non_explored_upload_rate)}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Non-explorers who uploaded<br />({sm.non_explored_uploaded ?? 0}/{sm.non_explored_users ?? 0})</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {Object.entries(choices).map(([k, v]) => <span key={k} className="rounded-sm bg-secondary px-2 py-1"><b className="font-mono">{v}</b> chose {k}</span>)}
              </div>
            </div>
          </div>

          {/* Devices */}
          <div className="border border-border bg-card p-6 rounded-sm" data-testid="funnel-devices">
            <div className="flex items-center gap-2 mb-3"><Monitor className="h-4 w-4 text-[#0B3D91]" /><h3 className="font-heading font-bold">Device split (signup / import)</h3></div>
            <div className="flex flex-wrap gap-3">
              {Object.keys(devices).length === 0 ? <span className="text-sm text-muted-foreground">No data yet.</span> :
                Object.entries(devices).map(([d, n]) => <span key={d} className="rounded-sm border border-border px-3 py-1.5 text-sm capitalize"><b className="font-mono">{n}</b> {d}</span>)}
            </div>
          </div>

          {/* Exit-survey: why users don't upload */}
          <div className="border border-border bg-card p-6 rounded-sm" data-testid="funnel-survey">
            <div className="flex items-center gap-2 mb-1"><MessageSquare className="h-4 w-4 text-[#0B3D91]" /><h3 className="font-heading font-bold">Why users aren't uploading (exit survey)</h3></div>
            <p className="text-xs text-muted-foreground mb-4">Self-reported barriers from trial users who haven't uploaded.</p>
            {survey?.stats && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4 text-center">
                <MiniStat label="Shown" value={survey.stats.shown} />
                <MiniStat label="Answered" value={survey.stats.submitted} green />
                <MiniStat label="Dismissed" value={survey.stats.dismissed} />
                <MiniStat label="Response" value={`${survey.stats.response_rate}%`} />
                <MiniStat label="Dismissal" value={`${survey.stats.dismissal_rate}%`} />
              </div>
            )}
            {(survey?.breakdown || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No survey responses yet — they'll appear here as abandoning users answer.</p>
            ) : (
              <div className="space-y-3">
                {survey.breakdown.map((b) => (
                  <div key={b.code} data-testid={`survey-reason-${b.code}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-foreground">{b.label}</span>
                      <span className="text-muted-foreground"><b className="text-foreground font-mono">{b.count}</b> · {b.pct}%</span>
                    </div>
                    <div className="h-2.5 w-full bg-secondary rounded-sm overflow-hidden flex">
                      <div className="h-full bg-[#0B3D91]" style={{ width: `${b.count ? (100 * b.explored / b.count) : 0}%` }} title={`Sample explorers: ${b.explored}`} />
                      <div className="h-full bg-[#0ea5a4]" style={{ width: `${b.count ? (100 * b.not_explored / b.count) : 0}%` }} title={`Did not explore: ${b.not_explored}`} />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      <span><span className="inline-block h-2 w-2 rounded-sm bg-[#0B3D91] mr-1" />explored sample: <b className="text-foreground">{b.explored}</b></span>
                      <span><span className="inline-block h-2 w-2 rounded-sm bg-[#0ea5a4] mr-1" />did not: <b className="text-foreground">{b.not_explored}</b></span>
                      {Object.entries(b.devices || {}).map(([d, n]) => <span key={d} className="capitalize">· {d}: <b className="text-foreground">{n}</b></span>)}
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground pt-1"><span className="inline-block h-2 w-2 rounded-sm bg-[#0B3D91] mr-1" />blue = explored the sample first &nbsp;<span className="inline-block h-2 w-2 rounded-sm bg-[#0ea5a4] mr-1" />teal = did not. If a barrier clusters among sample-explorers, the demo may be surfacing it rather than resolving it.</p>
                {(survey.recent_other || []).length > 0 && (
                  <div className="mt-4 border-t border-border pt-3">
                    <div className="text-xs font-semibold text-muted-foreground mb-1.5">Recent "Other" comments</div>
                    <ul className="space-y-1 text-sm text-foreground">
                      {survey.recent_other.slice(-6).reverse().map((t, i) => <li key={i} className="border-l-2 border-[#0B3D91]/30 pl-2">“{t}”</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, green }) {
  return (
    <div className="border border-border rounded-sm p-3">
      <div className={`text-2xl font-extrabold font-mono ${green ? "text-[#16A34A]" : "text-foreground"}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function MiniStat({ label, value, green }) {
  return (
    <div className="border border-border rounded-sm px-2 py-2">
      <div className={`text-lg font-extrabold font-mono ${green ? "text-[#16A34A]" : "text-foreground"}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}
